<template>
  <div class="confirm-page">
    <Card size="large" class="confirm-card">
      <View v-if="state === 'confirming'" direction="column" gap="md" align="center" padding="lg">
        <LoadingIndicator />
        <Text variant="body-large" color="secondary">Confirming your new email…</Text>
      </View>

      <View v-else-if="state === 'success'" direction="column" gap="md" align="center" padding="lg">
        <IconTile icon="check-circle" size="large" color="green" shape="rounded" colored-icon />
        <Text variant="h2" align="center">Email updated</Text>
        <Text variant="body-large" color="secondary" align="center">Your new address is now your sign-in email.</Text>
        <Button :to="profileButtonRoute">{{ isAuthenticated ? 'Back to settings' : 'Sign in' }}</Button>
      </View>

      <View v-else direction="column" gap="md" align="center" padding="lg">
        <IconTile icon="alert-triangle" size="large" color="red" shape="rounded" colored-icon />
        <Text variant="h2" align="center">This link didn't work</Text>
        <Text variant="body-large" color="secondary" align="center">{{ errorMessage }}</Text>
        <Button :to="profileButtonRoute">{{ isAuthenticated ? 'Back to settings' : 'Sign in' }}</Button>
      </View>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useAuthStore } from '@tallo/auth';
import { Button, Card, extractErrorMessage, IconTile, LoadingIndicator, Text, View } from '@tallo/design-system';
import { profileService, useInvestorStore } from '@tallo/investor/investor';
import { AppRouteNames, profileRoute } from '@tallo/investor/navigation';

const route = useRoute();
const authStore = useAuthStore();
const investorStore = useInvestorStore();
const { isAuthenticated } = storeToRefs(authStore);

const state = ref<'confirming' | 'success' | 'error'>('confirming');
const errorMessage = ref('');

const profileButtonRoute = computed(() =>
  isAuthenticated.value ? profileRoute('general') : { name: AppRouteNames.SIGN_IN },
);

onMounted(async () => {
  const token = route.query.token as string;

  if (!token) {
    state.value = 'error';
    errorMessage.value = 'The link is missing its token. Use the full link from the email.';
    return;
  }

  try {
    await profileService.confirmEmailChange(token);
    // If this is the signed-in device, resync so the General tab shows the new email and drops
    // the pending banner without needing a hard reload
    if (isAuthenticated.value) {
      await investorStore.load().catch(() => undefined);
    }
    state.value = 'success';
  } catch (error) {
    state.value = 'error';
    errorMessage.value = extractErrorMessage(
      error,
      'The link is invalid or expired. Request a new one from your settings.',
    );
  }
});
</script>

<style scoped lang="scss">
.confirm-page {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--t-spacing-lg);
  background: var(--t-color-neutral-100);
}

.confirm-card {
  width: 100%;
  max-width: 30rem;
}
</style>
